import { formatTripDateRange } from "../../helpers/dates";

import currencyFormatter from "../../helpers/currencyFormatter";
import formatStage from "../../helpers/format-stage";

const formatOperatorOption = ({ operatorName, _id }) => ({
  label: operatorName,
  value: _id,
});

const formatProspectOption = ({
  _id,
  firstAndLastName,
  lastName,
  firstName,
  emails,
  companyName,
  assigneeId,
  assigneeName,
}) => {
  let name = firstAndLastName || [firstName, lastName].filter(Boolean).join(" ").trim();
  let label = `${name} - ${emails[0]}`;

  if (companyName) {
    label = `${label} - ${companyName}`;
  }

  if (assigneeName && assigneeId) {
    label = `${label} - ${assigneeName}`;
  }

  return {
    value: _id,
    label,
  };
};

const formatTripOption = ({ _id, tripTag, firstDepartureDate, routing, lastArrivalDate, clientId, clientName }) => ({
  value: _id,
  label: [
    tripTag,
    formatTripDateRange(firstDepartureDate, lastArrivalDate),
    routing ?? "-",
  ].filter(Boolean).join(" | "),
  clientId: clientId || null,
  clientName: clientName || null,
});

const formatContractOption = (
  {
    _id,
    contractTag,
    clientName,
    clientId,
    legs,
    pricing,
    trip,
  },
  includeTripInfo = false,
) => ({
  value: _id,
  label: [
    ...(
      includeTripInfo ?
        [
          trip?.tripTag,
          formatTripDateRange(trip?.firstDepartureDate, trip?.lastArrivalDate),
          trip?.routing ?? "-",
        ] :
        []
    ),
    contractTag,
    clientName,
    legs?.[0]?.aircraft?.name,
    pricing?.flightCharges ? currencyFormatter.format(pricing.flightCharges) : null,
  ].filter(Boolean).join(" | "),
  clientId: clientId || null,
  clientName: clientName || null,
  trip: trip || null,
});

const formatOFROption = (
  {
    _id,
    ofrTag,
    operator,
    aircraft,
    operatorPrice,
    trip,
  },
  includeTripInfo = false,
) => ({
  value: _id,
  label: [
    ...(
      includeTripInfo ?
        [
          trip?.tripTag,
          formatTripDateRange(trip?.firstDepartureDate, trip?.lastArrivalDate),
          trip?.routing ?? "-",
        ] :
        []
    ),
    ofrTag,
    operator?.name,
    aircraft?.name,
    operatorPrice ? currencyFormatter.format(operatorPrice) : null,
  ].filter(Boolean).join(" | "),
});

const STRIPE_OBJECT_TYPE_LABELS = {
  intent: "Intent",
  charge: "Charge",
  refund: "Refund",
  dispute: "Dispute",
};

const STRIPE_STATUS_LABELS = {
  intent: {
    requires_payment_method: "Awaiting Card",
    requires_confirmation: "Awaiting Confirmation",
    requires_action: "Awaiting Client Action",
    requires_capture: "Authorized",
    processing: "Processing",
    succeeded: "Charged",
    canceled: "Canceled",
  },
  charge: {
    pending: "Pending",
    succeeded: "Completed",
    failed: "Declined",
  },
  refund: {
    pending: "Pending",
    requires_action: "Awaiting Client Details",
    succeeded: "Completed",
    failed: "Failed",
    canceled: "Canceled",
  },
  dispute: {
    warning_needs_response: "Inquiry, Response Needed",
    warning_under_review: "Inquiry, Under Review",
    warning_closed: "Inquiry Closed",
    needs_response: "Response Needed",
    under_review: "Under Review",
    won: "Won",
    lost: "Lost",
  },
};

const STRIPE_UNSETTLED_STATUSES = {
  charge: ["pending"],
  refund: ["pending", "requires_action"],
  dispute: ["warning_needs_response", "warning_under_review"],
};

const stripeStatusLabel = (row) =>
  STRIPE_STATUS_LABELS[row?.objectType]?.[row?.status] || row?.status || "-";

const isStripeRowUnsettled = (row) =>
  (STRIPE_UNSETTLED_STATUSES[row?.objectType] || []).includes(row?.status);

const formatGroupedOptionsByStage = (data, formatter, ...rest) =>
  Object.entries(
    Object.groupBy(data, item => item.stage),
  ).map(
    ([stage, data]) => ({
      label: formatStage(stage),
      options: data.map(item => formatter(item, ...rest)),
      priority: Math.min(
        ...data.map((i) => (Number.isFinite(i?.priority) ? i.priority : Number.POSITIVE_INFINITY)),
      ),
    }),
  ).sort(
    (a, b) => a.priority - b.priority,
  ).map(
    ({ label, options }) => ({ label, options }),
  );

const STRIPE_TRANSFER_DESCRIPTION_REGEX = /^stripe transfer/i;

const isStripeTransferLineItem = (row) => {
  const description = row._isLineItem ? row.reference : row.item?.lineItems?.[0]?.description;

  return STRIPE_TRANSFER_DESCRIPTION_REGEX.test((description || "").trim());
};

const getDefaultLinkPaymentType = (row, paymentRecord = null) => {
  if (row?.objectType) {
    return row.objectType === "intent" ? "ccAuthorization" : "CC";
  }

  if (paymentRecord?.paymentType) {
    return paymentRecord.paymentType;
  }

  if (isStripeTransferLineItem(row)) {
    return "CC";
  }

  if (!row?._isLineItem) {
    return "Wire";
  }

  return row.contactName === "Stripe" ? "CC" : null;
};

const buildLinkNotes = (row) =>
  (row?.objectType
    ? [
      { label: "Cardholder", value: row.cardholderName },
      { label: "Reference", value: row.description },
    ]
    : [
      { label: "Contact Name", value: row?.contactName },
      { label: "Reference", value: row?.reference },
    ]
  ).map(({ label, value }) => `${label}: ${value || "'-'"}`).join("; ");

export {
  isStripeTransferLineItem,
  getDefaultLinkPaymentType,
  buildLinkNotes,
  formatOperatorOption,
  formatProspectOption,
  formatTripOption,
  formatContractOption,
  formatOFROption,
  formatGroupedOptionsByStage,
  STRIPE_OBJECT_TYPE_LABELS,
  STRIPE_STATUS_LABELS,
  STRIPE_UNSETTLED_STATUSES,
  stripeStatusLabel,
  isStripeRowUnsettled,
};
